import { OnboardingStep } from '@repo/shared/dtos/organization'
import { generateId } from '@repo/shared/uuid/uuid'
import { useAsyncData } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { SaveIcon } from 'lucide-react'
import { memo } from 'react'
import { IoSparklesSharp } from 'react-icons/io5'

import { CtaGeneric } from '@/components/assistant/messages/ctas/CtaGeneric'
import { CtaProfile } from '@/components/assistant/messages/ctas/CtaProfile'
import { adminStoreActions } from '@/store/actions'
import { AssistantFeedItem } from '@/store/assistant'
import { useStoreDispatch } from '@/store/hooks'

export const CtaHandler = memo(function ({
  index,
  isThinking,
  item,
}: {
  index: number
  isThinking: boolean
  item: AssistantFeedItem
}) {
  if (item.type !== 'cta') return null

  const { tc } = useT({ keyPrefix: 'assistant' })

  const dispatch = useStoreDispatch()

  const {
    data: [tempData],
  } = useAsyncData([(state) => state.assistant.tempData])

  switch (item.cta) {
    case 'profile':
      return <CtaProfile item={item} />

    case 'generateDescription':
      return (
        <CtaGeneric
          key={item.id}
          ctaItemAfterClick="replace"
          ctaTranskey="generateDescription"
          icon={IoSparklesSharp}
          isDisabled={isThinking}
          itemIndex={index}
          newItem={{
            generationId: item.generationId,
            type: 'processingMessage',
            transKeyWhileProcessing: 'generatingDescription',
            transKeyAfterProcessing: 'descriptionGenerated',
            id: generateId(),
            sentAt: new Date().getTime(),
          }}
          onClick={() => {
            void dispatch(
              adminStoreActions.organization.generateDescription({
                generationId: item.generationId,
              }),
            )
          }}
        />
      )

    case 'applyDescription':
      return (
        <div key={item.id} className="flex justify-between gap-4">
          <CtaGeneric
            buttonVariant="secondary"
            ctaItemAfterClick="replace"
            ctaTranskey="generateDescription"
            icon={IoSparklesSharp}
            isDisabled={isThinking}
            itemIndex={index}
            newItem={{
              generationId: item.generationId,
              type: 'processingMessage',
              transKeyWhileProcessing: 'generatingDescription',
              transKeyAfterProcessing: 'descriptionGenerated',
              id: generateId(),
              sentAt: new Date().getTime(),
            }}
            onClick={() => {
              void dispatch(
                adminStoreActions.organization.generateDescription({
                  generationId: item.generationId,
                }),
              )
            }}
          />
          <CtaGeneric
            ctaItemAfterClick="replace"
            ctaTranskey="applyDescription"
            icon={SaveIcon}
            isDisabled={isThinking}
            itemIndex={index}
            newItem={{
              generationId: item.generationId,
              type: 'message',
              transKeyContent: 'descriptionApplied',
              id: generateId(),
              sentAt: new Date().getTime(),
            }}
            onClick={() => {
              if (!tempData) return
              void dispatch(
                adminStoreActions.organization.update({
                  fields: { description: tempData.content },
                }),
              )
            }}
          />
        </div>
      )

    case 'generateMethodology':
      return (
        <CtaGeneric
          key={item.id}
          ctaItemAfterClick="replace"
          ctaTranskey="generateMethodology"
          icon={IoSparklesSharp}
          isDisabled={isThinking}
          itemIndex={index}
          newItem={{
            generationId: item.generationId,
            type: 'processingMessage',
            transKeyWhileProcessing: 'generatingMethodology',
            transKeyAfterProcessing: 'methodologyGenerated',
            id: generateId(),
            sentAt: new Date().getTime(),
          }}
          onClick={() => {
            void dispatch(
              adminStoreActions.organization.generateMethodology({
                generationId: item.generationId,
              }),
            )
          }}
        />
      )

    case 'applyMethodology':
      return (
        <div key={item.id} className="flex justify-between gap-4">
          <CtaGeneric
            buttonVariant="secondary"
            ctaItemAfterClick="replace"
            ctaTranskey="generateMethodology"
            icon={IoSparklesSharp}
            isDisabled={isThinking}
            itemIndex={index}
            newItem={{
              generationId: item.generationId,
              type: 'processingMessage',
              transKeyWhileProcessing: 'generatingMethodology',
              transKeyAfterProcessing: 'methodologyGenerated',
              id: generateId(),
              sentAt: new Date().getTime(),
            }}
            onClick={() => {
              void dispatch(
                adminStoreActions.organization.generateMethodology({
                  generationId: item.generationId,
                }),
              )
            }}
          />
          <CtaGeneric
            ctaItemAfterClick="replace"
            ctaTranskey="applyDescription"
            icon={SaveIcon}
            isDisabled={isThinking}
            itemIndex={index}
            newItem={{
              generationId: item.generationId,
              type: 'message',
              transKeyContent: 'methodologyApplied',
              id: generateId(),
              sentAt: new Date().getTime(),
            }}
            onClick={() => {
              if (!tempData) return
              void dispatch(
                adminStoreActions.organization.update({
                  fields: { methodology: tempData.content },
                }),
              )
            }}
          />
        </div>
      )

    case 'askForMethodology':
    case 'askForResources':
    case 'askForMembers':
    case 'askForComplete': {
      const step =
        item.cta === 'askForMethodology'
          ? OnboardingStep.METHODOLOGY
          : item.cta === 'askForResources'
            ? OnboardingStep.SOURCES_PRIVATE
            : item.cta === 'askForMembers'
              ? OnboardingStep.MEMBERS
              : OnboardingStep.COMPLETED
      return (
        <CtaGeneric
          key={item.id}
          ctaItemAfterClick="replace"
          ctaTranskey="continue"
          isDisabled={isThinking}
          itemIndex={index}
          newItem={{
            generationId: item.generationId,
            type: 'message',
            content: tc('continue'),
            id: generateId(),
            isFromUser: true,
            sentAt: new Date().getTime(),
          }}
          onClick={() => {
            void dispatch(
              adminStoreActions.onboarding.setNextStep({
                step,
              }),
            )
          }}
        />
      )
    }

    default:
      return null
  }
})
